import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, collectionGroup, query, orderBy, limit, getDocs, doc, getDoc, startAfter } from "firebase/firestore";
import { PaginationControls } from "../../../components/common/PaginationControls.js";

/**
 * [Admin] Growth Engine Tab
 * Monitors SEO/GEO Marketing Generation & Allows Regeneration.
 */
export const GrowthTab = async (container) => {
    const db = getFirestore();
    const functions = getFunctions();

    // State for Pagination
    const PAGE_SIZE = 20;
    let state = {
        data: [],
        currentPage: 1,
        totalCount: 0,
        cursors: [], // Store lastVisible doc for each page
        isLoading: false
    };

    container.innerHTML = `
        <div class="space-y-6">
            <!-- Header Stats -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="bg-white p-6 rounded-2xl border border-indigo-100 shadow-sm relative overflow-hidden">
                    <div class="absolute right-0 top-0 p-4 opacity-5">
                        <span class="material-symbols-outlined text-8xl">rocket_launch</span>
                    </div>
                    <h3 class="text-sm font-bold text-gray-500 uppercase tracking-wider">AI 마케팅 자산</h3>
                    <p class="text-3xl font-black text-indigo-900 mt-2" id="stat-total">-</p>
                    <p class="text-xs text-indigo-500 mt-1">Gemini 2.5 생성 완료</p>
                </div>
                
                <div class="bg-white p-6 rounded-2xl border border-green-100 shadow-sm relative overflow-hidden">
                    <div class="absolute right-0 top-0 p-4 opacity-5">
                        <span class="material-symbols-outlined text-8xl">check_circle</span>
                    </div>
                    <h3 class="text-sm font-bold text-gray-500 uppercase tracking-wider">검색엔진 신호</h3>
                    <div class="flex items-center gap-2 mt-2">
                         <span class="w-3 h-3 rounded-full bg-green-500 animate-pulse"></span>
                         <p class="text-xl font-black text-green-900" id="stat-signals">Sitemap Active</p>
                    </div>
                    <div class="flex gap-2 mt-3">
                         <a href="https://search.google.com/search-console" target="_blank" class="text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 hover:bg-green-100 transition-colors flex items-center gap-1">
                            <span class="material-symbols-outlined text-[10px]">open_in_new</span> GSC 이동
                         </a>
                         <button onclick="navigator.clipboard.writeText('https://ssulwha.world/sitemap.xml'); alert('사이트맵 주소가 복사되었습니다.');" class="text-xs bg-gray-50 text-gray-600 px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 transition-colors flex items-center gap-1">
                            <span class="material-symbols-outlined text-[10px]">content_copy</span> 사이트맵 복사
                         </button>
                    </div>
                </div>

                <div class="bg-white p-6 rounded-2xl border border-purple-100 shadow-sm flex items-center justify-center cursor-pointer hover:bg-purple-50 transition-colors" id="btn-refresh-growth">
                     <div class="text-center">
                        <span class="material-symbols-outlined text-3xl text-purple-600 mb-2">refresh</span>
                        <p class="text-sm font-bold text-purple-900">실시간 새로고침</p>
                     </div>
                </div>
            </div>

            <!-- Recent Activity Feed -->
            <div class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 class="font-bold text-gray-800 flex items-center gap-2">
                        <span class="w-2 h-6 bg-indigo-500 rounded-full"></span>
                        최근 생성된 마케팅 데이터
                    </h3>
                </div>
                <div class="overflow-x-auto min-h-[400px]">
                    <div id="growth-loading-overlay" class="hidden absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
                         <span class="loader-spin w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></span>
                    </div>
                    <table class="w-full text-left relative">
                        <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
                            <tr>
                                <th class="px-6 py-3">시간</th>
                                <th class="px-6 py-3">작품 / 에피소드</th>
                                <th class="px-6 py-3">AI 마케팅 카피 (제목/요약)</th>
                                <th class="px-6 py-3 text-right">관리</th>
                            </tr>
                        </thead>
                        <tbody id="growth-feed" class="divide-y divide-gray-100 text-sm">
                            <tr><td colspan="4" class="px-6 py-10 text-center text-gray-400">데이터를 불러오는 중...</td></tr>
                        </tbody>
                    </table>
                </div>
                <!-- Pagination -->
                <div id="growth-pagination" class="border-t border-gray-100 px-6 bg-gray-50/50"></div>
            </div>
        </div>
    `;

    // Load Data
    const loadFeed = async () => {
        const feedContainer = container.querySelector('#growth-feed');
        const paginationContainer = container.querySelector('#growth-pagination');

        if (state.isLoading) return;
        state.isLoading = true;

        // Show Loading UI (keep existing content but dim it, or show spinner if empty)
        if (state.data.length === 0) {
            feedContainer.innerHTML = '<tr><td colspan="4" class="px-6 py-10 text-center text-gray-400"><span class="loader-spin w-6 h-6 border-2 border-indigo-500 rounded-full animate-spin"></span> 로딩 중...</td></tr>';
        }

        try {
            // 1. Build Query — fetch more to account for filtered non-marketing docs
            const fetchSize = PAGE_SIZE * 3; // Over-fetch to fill page after filtering
            let q = query(
                collectionGroup(db, 'ai_metadata'),
                orderBy('updatedAt', 'desc'),
                limit(fetchSize)
            );

            // Apply Cursor for Pagination
            if (state.currentPage > 1) {
                const prevCursor = state.cursors[state.currentPage - 2];
                if (prevCursor) {
                    q = query(q, startAfter(prevCursor));
                }
            }

            // 2. Fetch Data
            const snapshot = await getDocs(q);
            const docs = snapshot.docs;

            // 3. Filter to marketing docs only
            const allFiltered = docs.filter(d => {
                const data = d.data();
                return data.marketing_title && data.marketing_title !== '제목 없음';
            });

            // Take only PAGE_SIZE for display
            state.data = allFiltered.slice(0, PAGE_SIZE);

            // Cursor: use the LAST raw doc that corresponds to our last displayed item
            // Find the raw doc index of our last displayed item
            if (state.data.length > 0) {
                const lastDisplayed = state.data[state.data.length - 1];
                const rawIdx = docs.indexOf(lastDisplayed);
                state.cursors[state.currentPage - 1] = docs[rawIdx];
            } else if (docs.length > 0) {
                // All fetched docs were filtered out — use last raw doc as cursor
                state.cursors[state.currentPage - 1] = docs[docs.length - 1];
            }

            // 4. Count total marketing docs (only on init/refresh)
            if (state.totalCount === 0 || state.currentPage === 1) {
                // Count all marketing docs by scanning (one-time, cached)
                const countQ = query(
                    collectionGroup(db, 'ai_metadata'),
                    orderBy('updatedAt', 'desc'),
                    limit(500) // reasonable upper bound
                );
                const countSnap = await getDocs(countQ);
                state.totalCount = countSnap.docs.filter(d => {
                    const data = d.data();
                    return data.marketing_title && data.marketing_title !== '제목 없음';
                }).length;
                container.querySelector('#stat-total').innerText = state.totalCount.toLocaleString();
            }

            // 5. Render
            if (state.data.length === 0) {
                feedContainer.innerHTML = '<tr><td colspan="4" class="px-6 py-10 text-center text-gray-400">표시할 마케팅 데이터가 없습니다. (이미지 분석 데이터 제외됨)</td></tr>';
            } else {
                let html = '';
                for (const docSnap of state.data) {
                    const data = docSnap.data();

                    // Filter: Only show doc if it has marketing_title (Basic Integrity Check)
                    // Note: collectionGroup fetches ALL 'ai_metadata', could include other types?
                    // Usually we name subcollections specific like 'geo_marketing'.
                    // If 'ai_metadata' is the subcollection name, then fine.
                    // Assuming structure: series/{id}/episodes/{id}/ai_metadata/geo_marketing

                    const pathParts = docSnap.ref.path.split('/');
                    const seriesId = pathParts[1];
                    let episodeId = 'Unknown';

                    if (pathParts.length >= 4) {
                        // path: series/SID/episodes/EID/ai_metadata/geo_marketing
                        // parts: 0/1/2/3/4/5
                        if (pathParts[2] === 'episodes') episodeId = pathParts[3];
                        else episodeId = 'Series Level'; // If series level metadata
                    }

                    const time = data.updatedAt ? new Date(data.updatedAt.toDate()).toLocaleString() : '방금 전';
                    const title = data.marketing_title || '제목 없음';
                    const desc = data.marketing_description || '내용 없음';

                    html += `
                        <tr class="hover:bg-indigo-50/30 transition-colors">
                            <td class="px-6 py-4 text-gray-500 text-xs whitespace-nowrap">${time}</td>
                            <td class="px-6 py-4">
                                <span class="font-mono text-xs text-gray-400 block">${seriesId}</span>
                                <span class="font-mono text-xs text-indigo-400 block truncate max-w-[100px]" title="${episodeId}">${episodeId}</span>
                            </td>
                            <td class="px-6 py-4">
                                <p class="font-bold text-gray-800 line-clamp-1">${title}</p>
                                <p class="text-xs text-gray-500 line-clamp-1">${desc}</p>
                            </td>
                            <td class="px-6 py-4 text-right">
                                <div class="flex items-center justify-end gap-1">
                                    <button onclick="event.stopPropagation(); window.copyEpisodeShareLink('${seriesId}', '${episodeId}', 'ko')" class="text-purple-600 hover:text-purple-800 text-[11px] font-bold px-1.5 py-1 rounded-lg hover:bg-purple-50 transition-colors whitespace-nowrap" title="한국어 공유 링크">
                                        🇰🇷
                                    </button>
                                    <button onclick="event.stopPropagation(); window.copyEpisodeShareLink('${seriesId}', '${episodeId}', 'en')" class="text-blue-600 hover:text-blue-800 text-[11px] font-bold px-1.5 py-1 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap" title="English share link">
                                        🇺🇸
                                    </button>
                                    <button onclick="window.openGrowthModal('${seriesId}', '${episodeId}')" class="text-indigo-600 hover:text-indigo-800 text-sm font-bold px-3 py-1 rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap">
                                        상세 보기
                                    </button>
                                </div>
                            </td>
                        </tr>
                    `;
                }
                feedContainer.innerHTML = html;
            }

            // 5. Render Pagination
            const totalPages = Math.ceil(state.totalCount / PAGE_SIZE) || 1;
            PaginationControls.render({
                target: paginationContainer,
                currentPage: state.currentPage,
                totalPages: totalPages,
                onPageChange: (newPage) => {
                    state.currentPage = newPage;
                    loadFeed();
                }
            });

            container.querySelector('#stat-signals').innerText = "정상 작동 중";

        } catch (e) {
            console.error(e);
            let msg = e.message;
            if (e.message.includes("requires an index")) {
                msg = `<a href="${e.message.match(/https:\/\/\S+/)[0]}" target="_blank" class="text-indigo-500 underline">인덱스 생성 필요 (클릭)</a>`;
            }
            feedContainer.innerHTML = `<tr><td colspan="4" class="px-6 py-10 text-center text-red-500">오류 발생: ${msg}</td></tr>`;
        } finally {
            state.isLoading = false;
        }
    };

    // Modal Logic (Global for onClick)
    // [VIRAL] Copy Episode Share Link Helper
    window.copyEpisodeShareLink = async (seriesId, episodeId, lang = 'ko') => {
        const epPath = (episodeId && episodeId !== 'Series Level' && episodeId !== 'Unknown')
            ? `/${episodeId}` : '';
        let shareUrl = `https://ssulwha.world/share/novel/${seriesId}${epPath}`;
        if (lang === 'en') shareUrl += '?lang=en';
        const langLabel = lang === 'en' ? '🇺🇸 English' : '🇰🇷 한국어';
        try {
            await navigator.clipboard.writeText(shareUrl);
            window.Modal.toast(`📤 ${langLabel} 공유 링크 복사됨!`);
        } catch (err) {
            window.Modal.alert(`주소를 복사해주세요:<br><br><span class="text-xs text-gray-500 select-all break-all">${shareUrl}</span>`, "링크 복사");
        }
    };

    window.openGrowthModal = async (seriesId, episodeId) => {
        // Fetch Data first
        try {
            // Handle Series Level vs Episode Level
            const seriesRef = doc(db, 'series', seriesId); // [FIX] Fetch Series for Cover

            let targetRef = null;
            let geoRef = null;

            if (episodeId === 'Series Level' || !episodeId || episodeId === 'Unknown') {
                // Series Level Metadata (if exists)
                // Currently we mostly do Episode Level.
                // If ID is weird, try to fetch series level geo.
                targetRef = seriesRef;
                geoRef = doc(seriesRef, 'ai_metadata', 'geo_marketing');
            } else {
                targetRef = doc(db, 'series', seriesId, 'episodes', episodeId);
                geoRef = doc(targetRef, 'ai_metadata', 'geo_marketing');
            }

            const [targetSnap, seriesSnap, geoSnap] = await Promise.all([
                getDoc(targetRef),
                getDoc(seriesRef),
                getDoc(geoRef)
            ]);

            if (!geoSnap.exists) {
                window.Modal.alert("AI 마케팅 데이터를 찾을 수 없습니다.", "오류");
                return;
            }

            const targetData = targetSnap.exists() ? targetSnap.data() : {};
            const seriesData = seriesSnap.exists() ? seriesSnap.data() : {};
            const geoData = geoSnap.data();

            // [FIX] Consolidate Cover Image
            let coverUrl = targetData.thumbnail;

            if (!coverUrl && (seriesData.type === 'webtoon' || seriesData.type === 'davinci' || seriesData.type === 'cine_novel')) {
                if (targetData.imageUrls && targetData.imageUrls.length > 0) {
                    coverUrl = targetData.imageUrls[0];
                }
            }
            coverUrl = coverUrl || seriesData.coverImage || seriesData.thumbnail || '/og.webp';

            // Render Modal
            const modalHtml = `
                <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" id="growth-modal">
                    <div class="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col md:flex-row overflow-hidden">
                        
                        <!-- Left: Preview (Google/Kakao Style) -->
                        <div class="w-full md:w-1/2 bg-gray-100 p-6 flex flex-col gap-4 items-center justify-center border-r border-gray-200 overflow-hidden min-w-0">
                            <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest self-start mb-1">검색 노출 미리보기 (Preview)</h3>
                            
                            <!-- KO Preview -->
                            <div id="growth-preview-ko" class="w-full max-w-[340px] space-y-3">
                                <div class="w-full bg-white p-4 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                    <div class="flex items-center gap-1">
                                        <div class="w-5 h-5 rounded-full bg-gray-200 flex-shrink-0"></div>
                                        <div class="flex flex-col min-w-0">
                                            <span class="text-xs text-gray-800">ssulwha.world</span>
                                            <span class="text-[10px] text-gray-500 truncate">https://ssulwha.world/share/...</span>
                                        </div>
                                    </div>
                                    <h4 class="text-blue-800 text-base hover:underline cursor-pointer line-clamp-2 mt-1 break-words">${geoData.marketing_title}</h4>
                                    <p class="text-xs text-gray-600 line-clamp-2 mt-1 break-words">${geoData.marketing_description}</p>
                                </div>
                                <div class="w-full bg-[#fce049] p-3 rounded-lg shadow-sm">
                                    <div class="flex gap-3">
                                        <div class="w-14 h-14 bg-gray-300 rounded overflow-hidden flex-shrink-0">
                                            <img src="${coverUrl}" class="w-full h-full object-cover" onerror="this.src='/og.webp'">
                                        </div>
                                        <div class="flex-1 min-w-0 flex flex-col justify-center">
                                            <h4 class="text-sm font-bold text-black truncate">${geoData.marketing_title}</h4>
                                            <p class="text-[11px] text-black/70 line-clamp-2 mt-0.5">${geoData.marketing_description}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- EN Preview -->
                            <div id="growth-preview-en" class="hidden w-full max-w-[340px] space-y-3">
                                <div class="w-full bg-white p-4 rounded-lg shadow-sm border border-blue-200 overflow-hidden">
                                    <div class="flex items-center gap-1">
                                        <div class="w-5 h-5 rounded-full bg-gray-200 flex-shrink-0"></div>
                                        <div class="flex flex-col min-w-0">
                                            <span class="text-xs text-gray-800">ssulwha.world</span>
                                            <span class="text-[10px] text-gray-500 truncate">...?lang=en</span>
                                        </div>
                                    </div>
                                    <h4 class="text-blue-800 text-base hover:underline cursor-pointer line-clamp-2 mt-1 break-words">${geoData.marketing_title_en || geoData.marketing_title}</h4>
                                    <p class="text-xs text-gray-600 line-clamp-2 mt-1 break-words">${geoData.marketing_description_en || geoData.marketing_description}</p>
                                </div>
                                <div class="w-full bg-[#fce049] p-3 rounded-lg shadow-sm">
                                    <div class="flex gap-3">
                                        <div class="w-14 h-14 bg-gray-300 rounded overflow-hidden flex-shrink-0">
                                            <img src="${coverUrl}" class="w-full h-full object-cover" onerror="this.src='/og.webp'">
                                        </div>
                                        <div class="flex-1 min-w-0 flex flex-col justify-center">
                                            <h4 class="text-sm font-bold text-black truncate">${geoData.marketing_title_en || geoData.marketing_title}</h4>
                                            <p class="text-[11px] text-black/70 line-clamp-2 mt-0.5">${geoData.marketing_description_en || geoData.marketing_description}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Link Copy Buttons -->
                            <div class="w-full max-w-[340px] flex gap-2 mt-1">
                                <button id="btn-copy-ko-link" class="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1">
                                    🇰🇷 한국어 링크
                                </button>
                                <button id="btn-copy-en-link" class="flex-1 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-blue-700 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1">
                                    🇺🇸 English 링크
                                </button>
                            </div>

                        </div>

                        <!-- Right: Edit & Action -->
                        <div class="w-full md:w-1/2 p-8 flex flex-col">
                            <div class="flex justify-between items-start mb-6">
                                <div>
                                    <h2 class="text-2xl font-bold text-gray-900">AI 마케팅 자산</h2>
                                    <p class="text-sm text-gray-500">생성된 메타데이터 상세</p>
                                </div>
                                <button onclick="document.getElementById('growth-modal').remove()" class="p-2 hover:bg-gray-100 rounded-full">
                                    <span class="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div class="space-y-4 flex-grow">
                                <div>
                                    <div class="flex items-center gap-2 mb-3">
                                        <button class="growth-lang-tab px-3 py-1 rounded-lg text-xs font-bold bg-indigo-600 text-white transition-all" data-lang="ko">🇰🇷 한국어</button>
                                        <button class="growth-lang-tab px-3 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-500 hover:bg-gray-200 transition-all" data-lang="en">🇺🇸 English</button>
                                    </div>
                                    <div id="growth-ko-section">
                                        <label class="block text-xs font-bold text-gray-500 uppercase mb-1">마케팅 제목 (Title)</label>
                                        <div class="p-3 bg-gray-50 rounded-lg text-sm font-medium text-gray-800 border border-gray-200">
                                            ${geoData.marketing_title}
                                        </div>
                                    </div>
                                    <div id="growth-en-section" class="hidden">
                                        <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Marketing Title (EN)</label>
                                        <div class="p-3 bg-blue-50 rounded-lg text-sm font-medium text-gray-800 border border-blue-200">
                                            ${geoData.marketing_title_en || '<span class="text-gray-400 italic">영어 카피 없음 — 재생성하면 자동 생성됩니다</span>'}
                                        </div>
                                        ${geoData.marketing_description_en ? `
                                        <label class="block text-xs font-bold text-gray-500 uppercase mb-1 mt-3">Description (EN)</label>
                                        <div class="p-3 bg-blue-50 rounded-lg text-sm text-gray-700 border border-blue-200">
                                            ${geoData.marketing_description_en}
                                        </div>` : ''}
                                    </div>
                                </div>
                                <div>
                                    <label class="block text-xs font-bold text-gray-500 uppercase mb-1">추천 해시태그</label>
                                    <div class="flex flex-wrap gap-2">
                                        ${(geoData.hashtags || []).map(tag => `<span class="px-2 py-1 bg-indigo-50 text-indigo-600 text-xs rounded-md font-bold">${tag}</span>`).join('')}
                                    </div>
                                </div>
                                <div>
                                    <label class="block text-xs font-bold text-gray-500 uppercase mb-1">지역/테마 키워드</label>
                                    <div class="flex flex-wrap gap-2">
                                        ${(geoData.geo_keywords || []).map(k => `<span class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">${k}</span>`).join('')}
                                    </div>
                                </div>
                            </div>

                            <div class="mt-8 pt-6 border-t border-gray-100">
                                <button id="btn-regenerate" class="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200 font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
                                    <span class="material-symbols-outlined">autorenew</span>
                                    마케팅 문구 재생성 (Re-make)
                                </button>
                                <p class="text-center text-xs text-gray-400 mt-2">Gemini 2.5 Flash로 새로운 문구를 다시 작성합니다.</p>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            document.body.insertAdjacentHTML('beforeend', modalHtml);

            // [BILINGUAL] Language tab toggle in Growth modal (data + preview)
            document.querySelectorAll('.growth-lang-tab').forEach(tab => {
                tab.onclick = () => {
                    document.querySelectorAll('.growth-lang-tab').forEach(t => {
                        t.className = 'growth-lang-tab px-3 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-500 hover:bg-gray-200 transition-all';
                    });
                    tab.className = 'growth-lang-tab px-3 py-1 rounded-lg text-xs font-bold bg-indigo-600 text-white transition-all';
                    const lang = tab.dataset.lang;
                    document.getElementById('growth-ko-section').classList.toggle('hidden', lang !== 'ko');
                    document.getElementById('growth-en-section').classList.toggle('hidden', lang !== 'en');
                    document.getElementById('growth-preview-ko').classList.toggle('hidden', lang !== 'ko');
                    document.getElementById('growth-preview-en').classList.toggle('hidden', lang !== 'en');
                };
            });

            // [BILINGUAL] Link copy buttons
            const contentType = seriesData.type === 'webtoon' ? 'webtoon' : 'novel';
            const epPath = (episodeId && episodeId !== 'Series Level' && episodeId !== 'Unknown') ? '/' + episodeId : '';
            const baseShareUrl = 'https://ssulwha.world/share/' + contentType + '/' + seriesId + epPath;

            document.getElementById('btn-copy-ko-link').onclick = () => window.copyGrowthUrl(baseShareUrl);
            document.getElementById('btn-copy-en-link').onclick = () => window.copyGrowthUrl(baseShareUrl + '?lang=en');

            // [Util] Copy Helper
            window.copyGrowthUrl = async (url) => {
                try {
                    await navigator.clipboard.writeText(url);
                    if (window.Modal && window.Modal.alert) {
                        window.Modal.alert(`주소가 복사되었습니다!<br><br><span class="text-xs text-gray-500 break-all">${url}</span><br><br>카카오톡 등에 붙여넣기 해보세요.`, "복사 완료");
                    } else {
                        alert(`주소가 복사되었습니다!\n\n${url}\n\n카카오톡 등에 붙여넣기 해보세요.`);
                    }
                } catch (err) {
                    console.error("Clipboard Failed:", err);
                    if (window.Modal && window.Modal.prompt) {
                        window.Modal.prompt("주소 복사", url, () => { }, "Ctrl+C를 눌러 복사하세요");
                    } else {
                        prompt("PC에서는 Ctrl+C를 눌러 복사하세요:", url);
                    }
                }
            };

            // Bind Event
            document.getElementById('btn-regenerate').onclick = async function () {
                const btn = this;
                if (btn.disabled) return;

                const confirmed = await window.Modal.confirm("현재 마케팅 문구를 삭제하고 새로 생성하시겠습니까?\n(기존 내용은 사라집니다)", "마케팅 재생성");
                if (!confirmed) return;

                btn.disabled = true;
                btn.innerHTML = '<span class="loader-spin w-4 h-4 border-2 border-white rounded-full animate-spin"></span> 생성 중...';

                try {
                    const regenerateMarketing = httpsCallable(functions, 'regenerateMarketing');
                    await regenerateMarketing({ seriesId, episodeId });

                    await window.Modal.alert("✅ 재생성 완료!\n새로운 마케팅 문구가 적용되었습니다.", "완료");
                    document.getElementById('growth-modal').remove();

                    // Refresh Feed
                    if (container.querySelector('#btn-refresh-growth')) {
                        container.querySelector('#btn-refresh-growth').click();
                    }

                } catch (e) {
                    console.error(e);
                    window.Modal.alert("실패: " + e.message, "오류");
                    btn.disabled = false;
                    btn.innerHTML = '<span class="material-symbols-outlined">autorenew</span> 마케팅 문구 재생성 (Re-make)';
                }
            };

        } catch (e) {
            console.error(e);
            window.Modal.alert("오류: " + e.message, "오류");
        }
    };

    container.querySelector('#btn-refresh-growth').onclick = () => {
        state.currentPage = 1;
        state.cursors = [];
        state.totalCount = 0; // Force count refresh
        loadFeed();
    };

    loadFeed();
};
